import { Fragment } from 'react';

import {
  EPISODE_STATUSES,
  EPISODE_STATUS_LABELS,
  RECALL_TOOL_LABELS,
  THUMBS_DOWN_HINT,
  formatPartOf,
  formatShownCount,
  formatShownRate,
  hiddenNote,
  summaryVersionRows,
  thumbsDownComparison,
  type NeoRecallStats,
  type NeoRecallThumbsGroup,
} from '../neoRecallStats';

// The body of the "Past conversations" section on Neo chats (TOM-255), given
// the loaded numbers. Separate from the page so it renders without the API
// client (and in tests).
//
// Counts and rates only: the server sends no summary text, owner, conversation
// or id, so there is nothing here to open. A hidden number is a dash.

function ThumbsRow({ label, group }: { label: string; group: NeoRecallThumbsGroup }) {
  return (
    <tr>
      <td>{label}</td>
      <td>{formatShownCount(group.turns)}</td>
      <td>{formatShownCount(group.rated)}</td>
      <td>{formatShownCount(group.down)}</td>
      <td>{formatShownRate(group.rate)}</td>
    </tr>
  );
}

export function NeoRecallStatsView({ stats }: { stats: NeoRecallStats }) {
  const versions = summaryVersionRows(stats);
  const comparison = thumbsDownComparison(stats);
  const { recalled, forgotten, rate } = stats.recalledThenForgotten;

  return (
    <>
      <p className="admin-hint">
        What Neo remembers of owners&apos; past conversations, over the last {stats.windowDays}{' '}
        days. Counts across all owners only: no summary, owner or conversation is shown here.{' '}
        {hiddenNote(stats.minUsers)}
      </p>

      <dl className="admin-defs">
        <dt>Turns</dt>
        <dd>{formatShownCount(stats.turns.total)}</dd>
        <dt>Summaries on</dt>
        <dd>{formatShownCount(stats.turns.recallEnabled)}</dd>
        <dt>Carried a past summary</dt>
        <dd>{formatShownCount(stats.turns.withRecalledEpisode)}</dd>
        <dt>Carried, then forgotten</dt>
        <dd>{formatPartOf(forgotten, recalled, rate)}</dd>
        {stats.toolCalls.map((t) => (
          <Fragment key={t.tool}>
            <dt>{RECALL_TOOL_LABELS[t.tool]}</dt>
            <dd>
              {formatShownCount(t.calls)} <span className="admin-muted">calls</span>
            </dd>
          </Fragment>
        ))}
        <dt>Accounts with summaries off</dt>
        <dd>
          {formatShownCount(stats.usersRecallOff)} <span className="admin-muted">now</span>
        </dd>
        <dt>Learned facts held</dt>
        <dd>
          {formatShownCount(stats.profile.items)} <span className="admin-muted">now</span>
        </dd>
        <dt>Learned facts forgotten</dt>
        <dd>{formatShownCount(stats.profile.forgotten)}</dd>
      </dl>

      <h3>Thumbs-down</h3>
      <p className="admin-hint">{THUMBS_DOWN_HINT}</p>
      <div className="scroll-x">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Prompt carried</th>
              <th>Turns</th>
              <th>Rated</th>
              <th>Thumbs-down</th>
              <th>Rate</th>
            </tr>
          </thead>
          <tbody>
            <ThumbsRow label="A past summary" group={stats.thumbsDown.withRecall} />
            <ThumbsRow label="No past summary" group={stats.thumbsDown.withoutRecall} />
          </tbody>
        </table>
      </div>
      {comparison !== null ? <p className="admin-hint">{comparison}</p> : null}

      <h3>Summaries written</h3>
      <p className="admin-hint">
        By the summariser version that wrote them and where they are now. Skipped: nothing worth
        keeping, so no text was stored. Forgotten rate: the share of the rest their owner has
        forgotten.
      </p>
      {versions.length === 0 ? (
        <p className="admin-empty">No summaries written in this window.</p>
      ) : (
        <div className="scroll-x">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Summariser version</th>
                {EPISODE_STATUSES.map((status) => (
                  <th key={status}>{EPISODE_STATUS_LABELS[status]}</th>
                ))}
                <th>Forgotten rate</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.summarizerVersion}>
                  <td>
                    <code>{v.summarizerVersion}</code>
                  </td>
                  {EPISODE_STATUSES.map((status) => (
                    <td key={status}>{formatShownCount(v.counts[status])}</td>
                  ))}
                  <td>{formatShownRate(v.forgottenRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
